import { Box, Container, Typography, Button, Accordion, AccordionDetails, AccordionSummary, useTheme } from "@mui/material";
import { ExpandMoreRounded } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { theme } from "./theme";
import useTranslationState from "./useTranslationState";

function Consulting() {
    const navigate = useNavigate();

    const translations = useTranslationState({
        consulting: '',
        ourConsultingServices: '',
        consultingBigText: '',
        backToServices: 'Back to Services',
        conception: '',
        conceptionBigText: '',
        kitchenPlanning: '',
        kitchenPlanningBigText: '',
        menuPlanning: '',
        menuPlanningBigText: '',
        qualityManagement: '',
        qualityManagementBigText: '',
        diningRoomDesign: '',
        diningRoomDesignBigText: '',
    });

    return (
        <>
            {/* Hero Section */}
            <Box
                sx={{
                    width: '100%',
                    height: { xs: '50vh', md: '60vh' },
                    position: 'relative',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        zIndex: 1,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(66,79,82,0.9))',
                            zIndex: 2,
                        },
                    }}
                >
                    <Box
                        component="img"
                        src="https://picsum.photos/id/75/2400/1200"
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            filter: 'brightness(0.8)',
                        }}
                    />
                </Box>

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 3,
                        textAlign: 'center',
                        px: 3,
                        maxWidth: '900px',
                        opacity: 0,
                        animation: 'fadeInUp 1s forwards cubic-bezier(0.23, 1, 0.32, 1)',
                        '@keyframes fadeInUp': {
                            '0%': {
                                opacity: 0,
                                transform: 'translateY(30px)',
                            },
                            '100%': {
                                opacity: 1,
                                transform: 'translateY(0)',
                            },
                        },
                    }}
                >
                    <Typography
                        variant="h2"
                        component="h1"
                        sx={{
                            fontWeight: 600,
                            color: 'white',
                            letterSpacing: '-0.02em',
                            mb: 2,
                            textShadow: '0px 2px 4px rgba(0,0,0,0.15)',
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                        }}
                    >
                        {translations.consulting}
                    </Typography>
                    <Box
                        sx={{
                            width: '80px',
                            height: '3px',
                            background: theme.palette.primary.main,
                            mx: 'auto',
                            mb: 4,
                            borderRadius: '2px',
                        }}
                    />
                </Box>
            </Box>

            {/* Content Section */}
            <Box
                sx={{
                    width: '100%',
                    background: `linear-gradient(to bottom, ${theme.palette.primary.dark} 0%, #2d3638 100%)`,
                    py: { xs: 8, md: 12 },
                    color: 'white',
                }}
            >
                <Container maxWidth="md">
                    <Box
                        sx={{
                            opacity: 0,
                            animation: 'fadeIn 0.8s forwards cubic-bezier(0.23, 1, 0.32, 1)',
                            animationDelay: '0.4s',
                            '@keyframes fadeIn': {
                                '0%': {
                                    opacity: 0,
                                    transform: 'translateY(10px)',
                                },
                                '100%': {
                                    opacity: 1,
                                    transform: 'translateY(0)',
                                },
                            },
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 6,
                                lineHeight: 1.8,
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '1.1rem',
                                maxWidth: '700px',
                                mx: 'auto',
                            }}
                        >
                            {translations.consultingBigText}
                        </Typography>

                        <Box sx={{ mt: 5, mb: 6 }}>
                            <Typography
                                variant="h5"
                                component="h2"
                                sx={{
                                    fontWeight: 500,
                                    mb: 4,
                                    color: 'white',
                                    textAlign: 'center',
                                    position: 'relative',
                                    display: 'inline-block',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: '-10px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        width: '40px',
                                        height: '2px',
                                        backgroundColor: theme.palette.primary.main,
                                    }
                                }}
                            >
                                {translations.ourConsultingServices}
                            </Typography>

                            <Box
                                sx={{
                                    mt: 5,
                                    '& .MuiAccordion-root': {
                                        mb: 2,
                                        opacity: 0,
                                        animation: 'fadeIn 0.5s forwards cubic-bezier(0.23, 1, 0.32, 1)',
                                        animationDelay: 'calc(0.2s * var(--index))',
                                    },
                                    '& .MuiAccordion-root:nth-of-type(1)': {
                                        '--index': 1,
                                    },
                                    '& .MuiAccordion-root:nth-of-type(2)': {
                                        '--index': 2,
                                    },
                                    '& .MuiAccordion-root:nth-of-type(3)': {
                                        '--index': 3,
                                    },
                                    '& .MuiAccordion-root:nth-of-type(4)': {
                                        '--index': 4,
                                    },
                                    '& .MuiAccordion-root:nth-of-type(5)': {
                                        '--index': 5,
                                    },
                                }}
                            >
                                <Accordion
                                    sx={{
                                        border: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                        boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.08)',
                                        borderRadius: '12px !important',
                                        mb: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        backdropFilter: 'blur(10px)',
                                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreRounded sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                                        aria-controls="panel1a-content"
                                        id="panel1a-header"
                                        sx={{
                                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                color: 'white',
                                            }}
                                        >
                                            {translations.conception}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                lineHeight: 1.8,
                                                color: 'rgba(255,255,255,0.8)',
                                            }}
                                        >
                                            {translations.conceptionBigText}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion
                                    sx={{
                                        border: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                        boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.08)',
                                        borderRadius: '12px !important',
                                        mb: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        backdropFilter: 'blur(10px)',
                                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreRounded sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                                        aria-controls="panel2a-content"
                                        id="panel2a-header"
                                        sx={{
                                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                color: 'white',
                                            }}
                                        >
                                            {translations.kitchenPlanning}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                lineHeight: 1.8,
                                                color: 'rgba(255,255,255,0.8)',
                                            }}
                                        >
                                            {translations.kitchenPlanningBigText}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion
                                    sx={{
                                        border: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                        boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.08)',
                                        borderRadius: '12px !important',
                                        mb: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        backdropFilter: 'blur(10px)',
                                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreRounded sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                                        aria-controls="panel3a-content"
                                        id="panel3a-header"
                                        sx={{
                                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                color: 'white',
                                            }}
                                        >
                                            {translations.menuPlanning}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                lineHeight: 1.8,
                                                color: 'rgba(255,255,255,0.8)',
                                            }}
                                        >
                                            {translations.menuPlanningBigText}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion
                                    sx={{
                                        border: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                        boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.08)',
                                        borderRadius: '12px !important',
                                        mb: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        backdropFilter: 'blur(10px)',
                                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreRounded sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                                        aria-controls="panel4a-content"
                                        id="panel4a-header"
                                        sx={{
                                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                color: 'white',
                                            }}
                                        >
                                            {translations.qualityManagement}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                lineHeight: 1.8,
                                                color: 'rgba(255,255,255,0.8)',
                                            }}
                                        >
                                            {translations.qualityManagementBigText}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion
                                    sx={{
                                        border: 'none',
                                        '&:before': {
                                            display: 'none',
                                        },
                                        boxShadow: '0px 3px 12px rgba(0, 0, 0, 0.08)',
                                        borderRadius: '12px !important',
                                        mb: 2,
                                        overflow: 'hidden',
                                        backgroundColor: 'rgba(255,255,255,0.03)',
                                        backdropFilter: 'blur(10px)',
                                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreRounded sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                                        aria-controls="panel5a-content"
                                        id="panel5a-header"
                                        sx={{
                                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 500,
                                                color: 'white',
                                            }}
                                        >
                                            {translations.diningRoomDesign}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                lineHeight: 1.8,
                                                color: 'rgba(255,255,255,0.8)',
                                            }}
                                        >
                                            {translations.diningRoomDesignBigText}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                mt: 6,
                            }}
                        >
                            <Button
                                onClick={() => navigate("/")}
                                variant="outlined"
                                sx={{
                                    mt: 2,
                                    borderRadius: '8px',
                                    padding: '12px 28px',
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    borderColor: 'rgba(255,255,255,0.3)',
                                    color: 'white',
                                    boxShadow: 'none',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        borderColor: 'white',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                        transform: 'translateY(-3px)',
                                        backgroundColor: 'rgba(255,255,255,0.08)',
                                    }
                                }}
                            >
                                {translations.backToServices}
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </>
    );
}

export default Consulting; 
